/**
 * Blog API Service
 * Handles all blog and author CRUD operations
 */

import adminApi from './adminApi';
import type {
  Blog,
  BlogListResponse,
  BlogCreateDTO,
  BlogAuthor,
  BlogCategory,
  AuthorCreateDTO,
} from '../types/blog';

class BlogApiService {
  // ==================== BLOG POSTS ====================

  async getBlogs(page = 0, size = 10): Promise<BlogListResponse> {
    return adminApi.fetch(`/blogs?page=${page}&size=${size}`, {}, false);
  }

  async getBlogById(id: number): Promise<Blog> {
    return adminApi.fetch(`/blogs/${id}`, {}, false);
  }

  async getBlogBySlug(slug: string): Promise<Blog> {
    return adminApi.fetch(`/blogs/slug/${encodeURIComponent(slug)}`, {}, false);
  }

  async searchBlogs(keyword: string, page = 0, size = 10): Promise<BlogListResponse> {
    return adminApi.fetch(
      `/blogs/search?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`,
      {},
      false
    );
  }

  async getBlogsByAuthor(authorId: number, page = 0): Promise<BlogListResponse> {
    return adminApi.fetch(`/blogs/author/${authorId}?page=${page}`, {}, false);
  }

  async createBlog(data: BlogCreateDTO): Promise<Blog> {
    return adminApi.fetch('/blogs', {
      method: 'POST',
      data,
    });
  }

  async updateBlog(id: number, data: BlogCreateDTO): Promise<Blog> {
    return adminApi.fetch(`/blogs/${id}`, {
      method: 'PUT',
      data,
    });
  }

  async deleteBlog(id: number): Promise<void> {
    return adminApi.fetch(`/blogs/${id}`, { method: 'DELETE' });
  }

  async toggleBlogFooterActive(id: number, activeFooter?: boolean): Promise<Partial<Blog>> {
    const hasExplicitValue = typeof activeFooter === 'boolean';
    const valueQuery = hasExplicitValue ? `?value=${activeFooter}` : '';

    if (hasExplicitValue) {
      try {
        return await adminApi.fetch(`/blogs/${id}/active-footer${valueQuery}`, {
          method: 'PATCH',
        });
      } catch {
        // Fall back to toggle endpoint for backend variants that do not accept value query params.
      }
    }

    try {
      return await adminApi.fetch(`/blogs/${id}/toggle-active-footer`, {
        method: 'PATCH',
      });
    } catch (error) {
      try {
        return await adminApi.fetch(`/blogs/${id}/active-footer`, {
          method: 'PATCH',
          ...(hasExplicitValue ? { data: { value: activeFooter } } : {}),
        });
      } catch {
        throw error;
      }
    }
  }

  // ==================== BLOG CATEGORIES ====================

  async getBlogCategories(): Promise<BlogCategory[]> {
    return adminApi.fetch('/blogs/category', {}, false);
  }

  async createBlogCategory(name: string): Promise<BlogCategory> {
    return adminApi.fetch('/blogs/category', {
      method: 'POST',
      data: { name },
    });
  }

  // ==================== AUTHORS ====================

  async getAuthors(): Promise<BlogAuthor[]> {
    return adminApi.fetch('/authors', {}, false);
  }

  async getAuthorById(id: number): Promise<BlogAuthor> {
    return adminApi.fetch(`/authors/${id}`, {}, false);
  }

  async getAuthorByEmail(email: string): Promise<BlogAuthor> {
    return adminApi.fetch(`/authors/email/${encodeURIComponent(email)}`, {}, false);
  }

  async createAuthor(data: AuthorCreateDTO): Promise<BlogAuthor> {
    return adminApi.fetch('/authors', {
      method: 'POST',
      data,
    });
  }

  async updateAuthor(id: number, data: AuthorCreateDTO): Promise<BlogAuthor> {
    return adminApi.fetch(`/authors/${id}`, {
      method: 'PUT',
      data,
    });
  }

  async deleteAuthor(id: number): Promise<void> {
    return adminApi.fetch(`/authors/${id}`, { method: 'DELETE' });
  }
}

const blogApiService = new BlogApiService();
export default blogApiService;
